/**
 * ContactDetail — one contact's full CRM record.
 *
 * A contact in Baatly IS a Conversation document, so "everything we know about
 * this person" is spread across that document (identity, tags, custom
 * attributes, notes, campaign engagement) plus two sibling collections keyed by
 * the same phone number: Lead (the opportunity/pipeline record) and Appointment.
 * This page is the one place all of it is assembled.
 *
 * Left column = the contact card (identity + quick actions). Right column =
 * the CRM record, one Card per concern. Everything the APIs actually accept is
 * editable in place; everything else renders read-only rather than offering an
 * edit that would silently fail — see the per-card comments for which is which.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Archive as ArchiveIcon,
  AssignmentInd,
  CampaignOutlined,
  CheckCircleOutline,
  Check,
  Close,
  Edit as EditIcon,
  EventOutlined,
  FileDownloadOutlined,
  History,
  InfoOutlined,
  Label,
  LocalOffer,
  QuestionAnswerOutlined,
  StickyNote2Outlined,
  TrendingUp,
  Tune,
  ExpandMore,
  EventAvailableOutlined,
  KeyboardArrowDown,
  ChatBubbleOutline,
  Language as LanguageIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  PersonOutline,
} from '@mui/icons-material';

import PageHeader from '@/components/ui/PageHeader';
import StatusChip from '@/components/ui/StatusChip';
import SourceChip from '@/components/ui/SourceChip';
import DynamicFields from '@/components/common/DynamicFields';
import ContactNotesEditor from '@/components/contacts/ContactNotesEditor';
import ContactTagsEditor from '@/components/contacts/ContactTagsEditor';
import { formatPhoneDisplay } from '@/components/ui/PhoneField';
import { CountryEditRow, PhoneEditRow, RowSaveError } from '@/components/contacts/ContactIdentityEditors';
import { timeAgo } from '@/pages/admin/connectors/connectorUi';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket, LeadScoredEvent } from '@/hooks/useWebSocket';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import adminApi, {
  AppointmentSummary,
  ContactActivityItem,
  ContactDetail as ContactRecord,
  FieldDefinition,
  TeamMember,
} from '@/services/admin.api';

/** The forward pipeline. `lost`/`archived` are terminal and render as a chip
 *  instead of a step — they aren't a further stage, they're an exit. */
const PIPELINE_STAGES = ['new', 'contacted', 'qualified', 'converted'] as const;
const TERMINAL_STAGES = ['unqualified', 'lost', 'archived'] as const;

/** The feed opens on the latest 5 only; the rest is fetched from the API on demand when
 *  "Show more" is clicked (first click jumps to 25, then it doubles, up to the API's 200 cap). */
const ACTIVITY_PAGE_SIZE = 5;
const ACTIVITY_MAX_LIMIT = 200;
const nextActivityLimit = (current: number) =>
  Math.min(current <= ACTIVITY_PAGE_SIZE ? 25 : current * 2, ACTIVITY_MAX_LIMIT);

interface Lead {
  _id: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  status: string;
  priority?: string;
  score?: number;
  title?: string;
  /** What AI/widget-captured leads fill in instead of `title`. */
  interest?: string;
  estimatedValue?: number;
  createdAt?: string;
}

/** One append-only submission against a lead. Rendered under its lead so this
 *  page answers "what have they actually asked for over time", which the
 *  single-row pipeline card alone could not. */
interface LeadEnquiryRow {
  _id: string;
  createdAt: string;
  ingestionPath?: string;
  source?: string;
  title?: string;
  interest?: string;
  description?: string;
  budget?: string;
  notes?: string;
  tags?: string[];
}

/** Digits-only, matching the backend's normalizePhone — the join key between a
 *  contact and its leads/appointments, none of which store a normalized copy. */
function normalizePhone(raw?: string | null): string {
  return String(raw || '').replace(/\D/g, '');
}

/** ISO-3166 alpha-2 -> the flag emoji, via the regional-indicator block. No
 *  lookup table and no dependency; returns '' for anything that isn't two
 *  letters, so an unexpected value simply renders no flag. */
function flagEmoji(countryCode?: string): string {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Human labels for the audit `changedKeys` this page actually surfaces. Any
 *  key without an entry falls back to its raw name — a new audited field shows
 *  up in the feed immediately rather than silently disappearing. */
const ACTIVITY_KEY_LABELS: Record<string, string> = {
  customerName: 'name',
  customerEmail: 'email',
  customerPhone: 'phone',
  customerCountry: 'country',
  customerCountryCode: 'country',
  customAttributes: 'custom fields',
  tags: 'tags',
  notes: 'notes',
  status: 'status',
  priority: 'priority',
  score: 'score',
  title: 'title',
  assignedTo: 'assigned to',
};

/** Keys worth showing an explicit `from → to` for. Everything else is listed by
 *  name only — a custom-attributes or notes diff is noise in a one-line summary. */
const ACTIVITY_DIFF_KEYS = new Set([
  'tags',
  'status',
  'priority',
  'customerName',
  'customerCountry',
  'customerCountryCode',
]);

function activityValue(v: unknown): string {
  if (v === undefined || v === null || v === '') return 'none';
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'none';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function changedKeyPhrase(change: { key: string; from?: unknown; to?: unknown }): string {
  const label = ACTIVITY_KEY_LABELS[change.key] || change.key;
  if (!ACTIVITY_DIFF_KEYS.has(change.key)) return label;
  return `${label}: ${activityValue(change.from)} → ${activityValue(change.to)}`;
}

/** One-line summary for a feed row. Built client-side from the raw audit shape
 *  so the API stays a data endpoint rather than a copy endpoint. */
function describeActivity(item: ContactActivityItem): string {
  if (item.kind === 'note') return 'Note added';

  // "by {actor.name}" is already rendered separately below every row (same
  // treatment every other item gets), so these summaries don't repeat it —
  // for a system-initiated one, actor.name is already 'Assignment rules' /
  // 'Automatic escalation' (see contact.service.ts's getActivity), so that
  // line reads correctly either way.
  if (item.kind === 'assignment') {
    // `viaRules` is the ONLY signal telling "the actor below picked this
    // target directly" apart from "the actor below re-ran the assignment
    // rules and a rule picked it" — both carry the same real actor name, so
    // without this suffix the two read as identical lines. See
    // ContactActivityItem.viaRules' own doc comment.
    const rulesSuffix = item.viaRules ? ' (assignment rules applied)' : '';
    switch (item.action) {
      case 'assignment_reassigned':
        return `Reassigned from ${item.previousAssigneeName || 'a previous team member'} to ${item.assigneeName || 'a team member'}${rulesSuffix}`;
      case 'assignment_assigned':
        return `Assigned to ${item.assigneeName || 'a team member'}${rulesSuffix}`;
      case 'assignment_unassigned':
        return 'Unassigned';
      case 'assignment_claimed':
        return 'Claimed this conversation';
      case 'assignment_escalated':
        return item.assigneeName ? `Escalated to ${item.assigneeName}` : 'Escalated — no agent available';
      case 'assignment_handed_back':
        return 'Handed back to bot';
      default:
        return 'Assignment updated';
    }
  }

  const noun = item.kind === 'lead' ? 'Lead' : 'Contact';
  if (item.action === 'created') return `${noun} created`;
  if (item.action === 'deleted') return `${noun} deleted`;
  if (item.action === 'archived') return item.kind === 'lead' ? 'Lead archived' : 'Archived';

  const changes = item.changedKeys || [];
  if (changes.length === 0) {
    // A restore is a plain 'updated' with no diff — only `reason` identifies it.
    if (item.reason === 'Reactivated') return 'Restored';
    return item.reason || `${noun} updated`;
  }
  if (item.kind === 'lead') {
    const stage = changes.find((c) => c.key === 'status');
    if (stage) return `Stage: ${activityValue(stage.from)} → ${activityValue(stage.to)}`;
    const priority = changes.find((c) => c.key === 'priority');
    if (priority) return `Priority: ${activityValue(priority.from)} → ${activityValue(priority.to)}`;
  }
  return `Updated ${changes.map(changedKeyPhrase).join(', ')}`;
}

function activityIcon(item: ContactActivityItem): React.ReactNode {
  if (item.kind === 'note') return <StickyNote2Outlined sx={{ fontSize: 15 }} />;
  if (item.kind === 'assignment') return <AssignmentInd sx={{ fontSize: 15 }} />;
  if (item.kind === 'lead') return <TrendingUp sx={{ fontSize: 15 }} />;
  if (item.action === 'archived') return <ArchiveIcon sx={{ fontSize: 15 }} />;
  if ((item.changedKeys || []).some((c) => c.key === 'tags')) return <Label sx={{ fontSize: 15 }} />;
  return <EditIcon sx={{ fontSize: 15 }} />;
}

function displayName(contact: ContactRecord): string {
  return contact.customerName || formatPhoneDisplay(contact.customerPhone) || 'Unknown contact';
}

/** Section card — shared chrome so every card on the right column reads the same:
 *  tinted icon tile + title on the left, optional action on the right. */
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, action, children }) => (
  <Card sx={{ mb: 2.5, borderColor: '#e8ecf3', boxShadow: '0 1px 2px rgba(15,23,42,0.03)' }}>
    <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha('#4f46e5', 0.08),
            color: '#4f46e5',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, fontSize: '0.9375rem' }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

/** One label/value row. Read-only unless `onSave` is supplied, in which case a
 *  pencil swaps the value for an inline TextField. */
const DetailRow: React.FC<{
  label: string;
  value?: string;
  placeholder?: string;
  helper?: string;
  onSave?: (next: string) => Promise<void>;
}> = ({ label, value, placeholder, helper, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const commit = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.25 }}
      >
        {label}
      </Typography>
      {editing ? (
        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={draft}
            disabled={saving}
            {...(placeholder ? { placeholder } : {})}
            {...(helper ? { helperText: helper } : {})}
            onChange={(e) => setDraft(e.target.value)}
          />
          <IconButton size="small" color="primary" onClick={commit} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => setEditing(false)} disabled={saving}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" fontWeight={500} color={value ? 'text.primary' : 'text.disabled'}>
            {value || '—'}
          </Typography>
          {onSave && (
            <Tooltip title={`Edit ${label.toLowerCase()}`}>
              <IconButton size="small" onClick={start}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
};

/** Pipeline accent. One hue, three intensities — done / current / upcoming — so the bar
 *  reads as progress rather than a rainbow of unrelated states. */
const PIPE = { current: '#4f46e5', doneBg: '#e0e7ff', doneFg: '#4338ca', todoBg: '#f1f5f9', todoFg: '#94a3b8' };

/** Chevron-style stage bar (HubSpot / Salesforce "path"): every segment is clickable to move
 *  the lead to that stage; stages before the current one show a tick. */
const PipelineStageBar: React.FC<{
  stages: readonly string[];
  current: string;
  disabled?: boolean;
  onSelect: (stage: string) => void;
}> = ({ stages, current, disabled, onSelect }) => {
  const idx = stages.indexOf(current);
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      {stages.map((stage, i) => {
        const done = i < idx;
        const isCurrent = i === idx;
        const first = i === 0;
        const last = i === stages.length - 1;
        const notch = 10;
        // Built explicitly (readable) rather than by string-juggling above.
        const points = [
          '0 0',
          last ? '100% 0' : `calc(100% - ${notch}px) 0`,
          last ? '100% 100%' : '100% 50%',
          last ? '100% 100%' : `calc(100% - ${notch}px) 100%`,
          '0 100%',
          ...(first ? [] : [`${notch}px 50%`]),
        ];
        return (
          <ButtonBase
            key={stage}
            disabled={Boolean(disabled)}
            onClick={() => !isCurrent && onSelect(stage)}
            aria-label={`Move to ${stage}`}
            sx={{
              flex: 1,
              minWidth: 0,
              height: 38,
              ml: first ? 0 : '-4px',
              px: 2.25,
              gap: 0.5,
              clipPath: `polygon(${points.join(', ')})`,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'capitalize',
              transition: 'background-color .15s, color .15s',
              bgcolor: isCurrent ? PIPE.current : done ? PIPE.doneBg : PIPE.todoBg,
              color: isCurrent ? '#fff' : done ? PIPE.doneFg : PIPE.todoFg,
              '&:hover:not(.Mui-disabled)': {
                bgcolor: isCurrent ? PIPE.current : alpha(PIPE.current, 0.16),
                color: isCurrent ? '#fff' : PIPE.doneFg,
              },
              '&.Mui-disabled': { opacity: 1, cursor: 'default' },
            }}
          >
            {done && <Check sx={{ fontSize: 14 }} />}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stage}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const PRIORITY_TONE: Record<string, { bg: string; fg: string }> = {
  low: { bg: '#f1f5f9', fg: '#475569' },
  medium: { bg: '#dbeafe', fg: '#1d4ed8' },
  high: { bg: '#fef3c7', fg: '#92400e' },
  hot: { bg: '#fee2e2', fg: '#b91c1c' },
};

/** Read-only band chip. Priority is derived from the score by the scoring engine (thresholds
 *  live in Lead Scoring settings), so it is displayed here but never edited by hand. */
const PriorityBadge: React.FC<{ value: string }> = ({ value }) => (
  <Chip
    size="small"
    label={value}
    sx={{
      bgcolor: PRIORITY_TONE[value]?.bg,
      color: PRIORITY_TONE[value]?.fg,
      fontWeight: 600,
      textTransform: 'capitalize',
    }}
  />
);

/** Small labelled field used in the pipeline's property row. */
const MiniLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ display: 'block', mb: 0.75, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
  >
    {children}
  </Typography>
);

const SOURCE_ICON: Record<string, React.ReactNode> = {
  whatsapp: <WhatsAppIcon sx={{ fontSize: 14 }} />,
  instagram: <InstagramIcon sx={{ fontSize: 14 }} />,
  webchat: <LanguageIcon sx={{ fontSize: 14 }} />,
  manual: <PersonOutline sx={{ fontSize: 14 }} />,
};

const STATUS_TONE: Record<string, string> = {
  requested: '#f59e0b',
  confirmed: '#3b82f6',
  completed: '#10b981',
  cancelled: '#ef4444',
  no_show: '#ef4444',
};

/** One appointment as a compact row: date tile · time + what · source · status. */
const AppointmentRow: React.FC<{ appt: AppointmentSummary; onOpen: () => void }> = ({ appt, onOpen }) => {
  const when = appt.requestedAt ? new Date(appt.requestedAt) : null;
  const valid = when && !isNaN(when.getTime());
  const tone = STATUS_TONE[appt.status] || '#94a3b8';
  const timeText = valid
    ? when!.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : appt.requestedText || 'Time not set';
  const dayText = valid
    ? when!.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : 'Date to be confirmed';
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 2,
        p: 1.5,
        textAlign: 'left',
        border: '1px solid #e8ecf3',
        borderRadius: 2.5,
        transition: 'border-color .15s, background-color .15s',
        '&:hover': { borderColor: alpha('#4f46e5', 0.4), bgcolor: '#fafaff' },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 56,
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(tone, 0.1),
          color: tone,
        }}
      >
        {valid ? (
          <>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1 }}>
              {when!.toLocaleDateString(undefined, { month: 'short' })}
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.25 }}>{when!.getDate()}</Typography>
          </>
        ) : (
          <EventAvailableOutlined />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {timeText}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {dayText}
          {appt.appointmentType ? ` · ${appt.appointmentType}` : ''}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: 'text.disabled' }}>
          {SOURCE_ICON[appt.source] || <ChatBubbleOutline sx={{ fontSize: 14 }} />}
          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
            via {appt.source === 'webchat' ? 'website chat' : appt.source}
          </Typography>
        </Stack>
      </Box>
      <StatusChip status={appt.status} dot label={appt.status.replace('_', ' ')} sx={{ textTransform: 'capitalize' }} />
    </ButtonBase>
  );
};

const ContactDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Same rule as AdminLayout/Dashboard: the app is mounted under both /admin and /.
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';

  const { hasPermission } = usePermissionAccess();
  const { hasModuleAccess } = useModuleAccess();
  const canEdit = hasPermission('contacts', 'edit');
  // DELETE /contacts/:id/notes/:noteId method-derives to 'full' server-side
  // (require-permission.middleware.ts) — a strictly higher tier than the
  // 'edit' canEdit checks, same distinction ChatLogs' ContactPanel makes.
  const canDeleteNotes = hasPermission('contacts', 'full');
  const canSeeLeads = hasModuleAccess('activity.leads');
  const canEditLeads = canSeeLeads && hasPermission('activity.leads', 'edit');
  const canSeeAppointments = hasModuleAccess('appointments');

  const [contact, setContact] = useState<ContactRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [actionError, setActionError] = useState('');

  const [definitions, setDefinitions] = useState<FieldDefinition[]>([]);
  const [definedValues, setDefinedValues] = useState<Record<string, string>>({});
  const [freeFormValues, setFreeFormValues] = useState<Record<string, string>>({});
  const [attrSaving, setAttrSaving] = useState(false);

  const [leads, setLeads] = useState<Lead[]>([]);
  // Live score/priority updates from the backend's background rescore.
  const { user: wsUser, tenant: wsTenant } = useAuth();
  useWebSocket(wsUser?.tenantId || wsTenant?.id, {
    onLeadScored: (data: LeadScoredEvent) => {
      const byId = new Map((data.leads ?? []).map((l) => [l.leadId, l]));
      if (!byId.size) return;
      setLeads((prev) =>
        prev.map((l) => {
          const u = byId.get(l._id);
          if (!u) return l;
          const next = { ...l, score: u.score };
          if (u.priority) next.priority = u.priority;
          return next;
        })
      );
    },
  });
  const [leadsLoading, setLeadsLoading] = useState(false);
  // Enquiry history per lead id. Fetched after the leads land (it needs their
  // ids) and kept keyed rather than flattened, since a contact can legitimately
  // hold more than one lead.
  const [leadEnquiries, setLeadEnquiries] = useState<Record<string, LeadEnquiryRow[]>>({});
  const [leadSavingId, setLeadSavingId] = useState('');
  const [creatingLead, setCreatingLead] = useState(false);

  const [appointments, setAppointments] = useState<AppointmentSummary[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiveSaving, setArchiveSaving] = useState(false);

  // ── Assignee editing ──────────────────────────────────────────────────────
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState('');
  const [assigneeOriginal, setAssigneeOriginal] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  // Assignment / routing events (assigned, escalated, handed back to bot…) are the noisiest
  // part of the feed — one escalation loop can bury every note and edit — so they sit
  // behind a toggle, off by default.
  const [showRouting, setShowRouting] = useState(false);
  const [activity, setActivity] = useState<ContactActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityLimit, setActivityLimit] = useState(ACTIVITY_PAGE_SIZE);
  const [closeMenu, setCloseMenu] = useState<{ el: HTMLElement; leadId: string } | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────

  // The server already drops routing events when the toggle is off (so `limit` counts rows the
  // user can actually see) — no client-side filtering needed.
  const visibleActivity = activity;

  const loadContact = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminApi.getContact(id);
      setContact(data);
      setLoadError('');
      setNotFound(false);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setNotFound(true);
      } else {
        setLoadError(e?.response?.data?.error?.message || e.message || 'Failed to load contact');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadContact();
  }, [loadContact]);

  const loadActivity = useCallback(
    async (limit: number) => {
      if (!id) return;
      setActivityLoading(true);
      try {
        const res = await adminApi.getContactActivity(
          id,
          { limit, includeRouting: showRouting },
          { suppressPermissionLock: true }
        );
        setActivity(res.items);
        setActivityHasMore(res.hasMore);
      } catch {
        // The feed is supporting context, never the reason the page exists —
        // a failure here leaves the record itself perfectly usable.
        setActivity([]);
        setActivityHasMore(false);
      } finally {
        setActivityLoading(false);
      }
    },
    [id, showRouting]
  );

  useEffect(() => {
    loadActivity(activityLimit);
  }, [loadActivity, activityLimit]);

  /** Every successful edit on this page routes through here, so the feed can
   *  never show a stale history next to the value that was just changed. */
  const refreshActivity = useCallback(() => {
    loadActivity(activityLimit);
  }, [loadActivity, activityLimit]);

  useEffect(() => {
    adminApi
      .getFieldDefinitions('contact', { suppressPermissionLock: true })
      .then((defs) => setDefinitions(defs || []))
      .catch(() => {});
  }, []);

  // Re-seed the custom-field editors from the contact every time it's re-read,
  // so a save elsewhere on the page can never leave stale drafts behind.
  useEffect(() => {
    if (!contact) return;
    const definedKeyMap = new Map(definitions.map((d) => [d.key.toLowerCase(), d.key]));
    const dv: Record<string, string> = {};
    const ff: Record<string, string> = {};
    for (const attr of contact.customAttributes || []) {
      const defKey = definedKeyMap.get(attr.key.toLowerCase());
      if (defKey) dv[defKey] = String(attr.value);
      else ff[attr.key] = String(attr.value);
    }
    setDefinedValues(dv);
    setFreeFormValues(ff);
  }, [contact, definitions]);

  const phone = contact?.customerPhone || '';
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  // Leads have no id link to the contact — they're matched by phone. The API
  // only does a regex `search`, so anything it returns is a candidate, not a
  // match: narrow to the EXACT normalized number here.
  useEffect(() => {
    if (!normalizedPhone || !canSeeLeads) return;
    let cancelled = false;
    setLeadsLoading(true);
    adminApi
      .getLeads({ search: normalizedPhone, limit: 50 }, { suppressPermissionLock: true })
      .then((res) => {
        if (cancelled) return;
        const matched = (res.data || []).filter(
          (l: Lead) => normalizePhone(l.customerPhone) === normalizedPhone
        );
        setLeads(matched);
        // Each lead's own submissions, fetched per lead because the endpoint is
        // scoped to one. Failures are swallowed per lead: the pipeline card is
        // the primary content here and must still render without its history.
        matched.forEach((l: Lead) => {
          adminApi
            .getLeadEnquiries(l._id, { page: 1, limit: 20 })
            .then((r) => {
              if (cancelled) return;
              setLeadEnquiries((prev) => ({ ...prev, [l._id]: (r.data || []) as LeadEnquiryRow[] }));
            })
            .catch(() => undefined);
        });
      })
      .catch(() => {
        if (!cancelled) setLeads([]);
      })
      .finally(() => {
        if (!cancelled) setLeadsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedPhone, canSeeLeads]);

  useEffect(() => {
    if (!normalizedPhone || !canSeeAppointments) return;
    let cancelled = false;
    setAppointmentsLoading(true);
    adminApi
      .getAppointments({ phone: normalizedPhone, limit: 10 }, { suppressPermissionLock: true })
      .then((res) => {
        if (!cancelled) setAppointments(res.items);
      })
      .catch(() => {
        if (!cancelled) setAppointments([]);
      })
      .finally(() => {
        if (!cancelled) setAppointmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedPhone, canSeeAppointments]);

  // ── Mutations (every one re-reads the contact so the page can't drift) ────

  useEffect(() => {
    adminApi
      .getTeamDirectory({ suppressPermissionLock: true })
      .then(setTeamMembers)
      .catch(() => setTeamMembers([]));
  }, []);

  const handleAssignContact = async () => {
    if (assigneeDraft === assigneeOriginal) return;
    setAssignSaving(true);
    try {
      if (assigneeDraft === '') {
        // Unassign: clear the assignment without re-applying rules.
        await adminApi.bulkUnassignContacts([id], false);
        setContact((prev) => (prev ? { ...prev, assignedTo: null } : prev));
      } else {
        await adminApi.assignConversation(id, assigneeDraft);
        // Optimistic: update the in-memory contact so the UI reflects the change immediately.
        const member = teamMembers.find((m) => m._id === assigneeDraft);
        setContact((prev) =>
          prev
            ? {
                ...prev,
                assignedTo: member
                  ? { _id: member._id, firstName: member.firstName, lastName: member.lastName, email: member.email }
                  : null,
              }
            : prev
        );
      }
      setEditingAssignee(false);
      refreshActivity();
      setToast('Assigned to updated');
    } catch (e: any) {
      setActionError(
        e?.response?.data?.error?.message || e.message || 'Failed to update assignment'
      );
    } finally {
      setAssignSaving(false);
    }
  };

  const saveField = async (payload: Parameters<typeof adminApi.updateContact>[1], label: string) => {
    try {
      await adminApi.updateContact(id, payload);
      await loadContact();
      refreshActivity();
      setToast(`${label} updated`);
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || `Failed to update ${label.toLowerCase()}`);
    }
  };

  /**
   * Like saveField, but for the identity fields (phone, country): the row needs the failure to
   * stay open on, so this RETURNS it instead of surfacing a page-level toast. A 409 carries the
   * contact that already holds the number, which becomes an "Open contact" link.
   */
  const patchContact = async (
    payload: Parameters<typeof adminApi.updateContact>[1],
    label: string
  ): Promise<RowSaveError | null> => {
    try {
      const res = await adminApi.updateContact(id, payload);
      await loadContact();
      refreshActivity();
      const moved = (res.relinked?.leads ?? 0) + (res.relinked?.appointments ?? 0);
      setToast(moved > 0 ? `${label} updated — ${moved} linked record${moved === 1 ? '' : 's'} moved with it` : `${label} updated`);
      return null;
    } catch (e: any) {
      const err = e?.response?.data?.error;
      const other = e?.response?.status === 409 ? err?.details?.contactId : undefined;
      return {
        message: err?.message || e?.message || `Failed to update ${label.toLowerCase()}`,
        ...(other
          ? { link: { label: 'Open contact', onClick: () => navigate(`${pathPrefix}/contacts/${other}`) } }
          : {}),
      };
    }
  };

  const saveCustomFields = async () => {
    // updateContact FULL-REPLACES customAttributes, so the payload must carry
    // the free-form entries too — sending only the defined ones would silently
    // delete every attribute this tenant hasn't declared a field definition for.
    const definedEntries = Object.entries(definedValues)
      .map(([key, value]) => ({ key: key.trim(), value: String(value).trim() }))
      .filter((e) => e.key && e.value);
    const definedKeysLower = new Set(definedEntries.map((e) => e.key.toLowerCase()));
    const freeEntries = Object.entries(freeFormValues)
      .map(([key, value]) => ({ key: key.trim(), value: String(value).trim() }))
      .filter((e) => e.key && e.value && !definedKeysLower.has(e.key.toLowerCase()));

    setAttrSaving(true);
    try {
      await adminApi.updateContact(id, { customAttributes: [...definedEntries, ...freeEntries] });
      await loadContact();
      refreshActivity();
      setToast('Custom fields updated');
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to save custom fields');
    } finally {
      setAttrSaving(false);
    }
  };

  const handleTagsSaved = () => {
    loadContact();
    refreshActivity();
    setToast('Tags updated');
  };

  const handleExport = async () => {
    if (!contact) return;
    try {
      const blob = await adminApi.exportConversation(contact._id, contact.channel || 'whatsapp');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contact-${normalizedPhone || contact._id}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to export');
    }
  };

  const handleArchiveToggle = async () => {
    if (!contact) return;
    setArchiveSaving(true);
    try {
      await adminApi.setContactStatus(contact._id, contact.status === 'archived' ? 'active' : 'archived');
      await loadContact();
      refreshActivity();
      setArchiveConfirm(false);
      setToast(contact.status === 'archived' ? 'Contact restored' : 'Contact archived');
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to update status');
    } finally {
      setArchiveSaving(false);
    }
  };

  const patchLead = async (leadId: string, patch: Record<string, string>) => {
    setLeadSavingId(leadId);
    try {
      const updated = await adminApi.updateLead(leadId, patch);
      setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, ...updated, ...patch } : l)));
      refreshActivity();
      setToast('Lead updated');
      // The score is recomputed by the backend ~3 s after the write — re-pull it then.
      for (const delayMs of [2500, 5500]) {
        window.setTimeout(() => {
          adminApi
            .getLeadById(leadId)
            .then((fresh) => {
              if (!fresh?._id) return;
              setLeads((prev) =>
                prev.map((l) => {
                  if (l._id !== leadId) return l;
                  const next = { ...l };
                  if (fresh.score !== undefined) next.score = fresh.score;
                  if (fresh.priority !== undefined) next.priority = fresh.priority;
                  return next;
                })
              );
            })
            .catch(() => {});
        }, delayMs);
      }
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to update lead');
    } finally {
      setLeadSavingId('');
    }
  };

  const handleCreateLead = async () => {
    if (!contact) return;
    setCreatingLead(true);
    try {
      const lead = await adminApi.createLead({
        ...(contact.customerName ? { customerName: contact.customerName } : {}),
        customerPhone: normalizedPhone,
        ...(contact.customerEmail ? { customerEmail: contact.customerEmail } : {}),
        source: 'manual',
        status: 'new',
      });
      setLeads((prev) => [lead as Lead, ...prev]);
      refreshActivity();
      setToast('Lead created');
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to create lead');
    } finally {
      setCreatingLead(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // A real POP restores the list URL that was left behind — which, now that the
  // list keeps its query there, means its filters, page and search come back
  // too. Only taken when we know a list entry is actually behind us: arriving by
  // bookmark, refresh or a direct link has no history to go back to, so those
  // fall through to the plain path.
  const backToList = () =>
    (location.state as { fromList?: boolean } | null)?.fromList
      ? navigate(-1)
      : navigate(`${pathPrefix}/contacts`);

  // ── Prev / Next through the list the user came from ───────────────────────
  // The ordered ids arrive via router state from Contacts.tsx, so stepping
  // follows the exact filtered + sorted view rather than some server-side
  // "next by id". Absent state (deep link, refresh, opened from elsewhere)
  // simply yields no siblings and the controls stay hidden.
  const siblingIds: string[] = Array.isArray((location.state as any)?.siblingIds)
    ? ((location.state as any).siblingIds as string[])
    : [];
  const siblingIndex = siblingIds.indexOf(id);
  const prevId = siblingIndex > 0 ? siblingIds[siblingIndex - 1] : undefined;
  const nextId =
    siblingIndex >= 0 && siblingIndex < siblingIds.length - 1
      ? siblingIds[siblingIndex + 1]
      : undefined;
  const cameFromList = Boolean((location.state as { fromList?: boolean } | null)?.fromList);
  const goToSibling = (targetId?: string) => {
    if (!targetId) return;
    // `replace` keeps Back pointing at the list instead of accumulating one
    // history entry per contact stepped through — so the history entry behind
    // this one is still the original list page, and `fromList` must carry
    // forward or a later Back (after stepping) would fall through to the
    // plain, unfiltered `/contacts` path instead of restoring it.
    navigate(`${pathPrefix}/contacts/${targetId}`, {
      replace: true,
      state: { fromList: cameFromList, siblingIds },
    });
  };

  if (notFound) {
    return (
      <Box>
        <PageHeader title="Contact not found" breadcrumbs={[{ label: 'Contacts', href: `${pathPrefix}/contacts` }]} />
        <Alert severity="warning" action={<Button onClick={backToList}>Back to contacts</Button>}>
          This contact no longer exists, or it belongs to a different workspace.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="Contact" breadcrumbs={[{ label: 'Contacts', href: `${pathPrefix}/contacts` }]} />
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Skeleton variant="circular" width={72} height={72} sx={{ mx: 'auto', mb: 2 }} />
                <Skeleton height={28} />
                <Skeleton height={20} width="60%" />
                <Skeleton height={20} width="80%" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            {[0, 1, 2].map((i) => (
              <Card key={i} sx={{ mb: 2.5 }}>
                <CardContent>
                  <Skeleton height={26} width="30%" />
                  <Skeleton height={20} />
                  <Skeleton height={20} width="70%" />
                </CardContent>
              </Card>
            ))}
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (loadError || !contact) {
    return (
      <Box>
        <PageHeader title="Contact" breadcrumbs={[{ label: 'Contacts', href: `${pathPrefix}/contacts` }]} />
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                setLoading(true);
                loadContact();
              }}
            >
              Retry
            </Button>
          }
        >
          {loadError || 'Failed to load contact'}
        </Alert>
      </Box>
    );
  }

  const isArchived = contact.status === 'archived';
  const engagement = contact.campaignEngagement;
  const hasEngagement = Boolean(
    engagement && ((engagement.attempts ?? 0) > 0 || (engagement.targetedCampaignIds?.length ?? 0) > 0)
  );
  const externalAttributes = contact.externalAttributes || [];
  const freeFormKeys = Object.keys(freeFormValues);
  const assigneeName = contact.assignedTo
    ? `${contact.assignedTo.firstName || ''} ${contact.assignedTo.lastName || ''}`.trim() ||
      contact.assignedTo.email
    : '';

  return (
    <Box>
      <PageHeader
        title={displayName(contact)}
        breadcrumbs={[{ label: 'Contacts', href: `${pathPrefix}/contacts` }, { label: displayName(contact) }]}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {siblingIndex >= 0 && siblingIds.length > 1 && (
              <>
                <Tooltip title={prevId ? 'Previous contact' : 'First contact in this view'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => goToSibling(prevId)}
                      disabled={!prevId}
                      aria-label="Previous contact"
                    >
                      <ChevronLeft />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'center' }}>
                  {siblingIndex + 1} of {siblingIds.length}
                </Typography>
                <Tooltip title={nextId ? 'Next contact' : 'Last contact in this view'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => goToSibling(nextId)}
                      disabled={!nextId}
                      aria-label="Next contact"
                    >
                      <ChevronRight />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={backToList}>
              Back
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2.5}>
        {/* ── LEFT: the contact card ─────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          {/* Sticky, viewport-tall rail: the profile card keeps its natural height and the Activity
              card takes ALL the remaining height and scrolls inside itself. The right column is
              much taller than this one, so without this the page showed a big empty gap under the
              feed; now the rail stays in view while the record scrolls and the feed fills it. */}
          <Box
            sx={{
              position: { xs: 'static', md: 'sticky' },
              top: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              height: { md: 'calc(100vh - 32px)' },
              maxHeight: { md: 'calc(100vh - 32px)' },
            }}
          >
          <Card sx={{ flexShrink: 0 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  mx: 'auto',
                  mb: 1.5,
                  bgcolor: alpha('#4f46e5', 0.12),
                  color: '#4f46e5',
                  fontSize: 28,
                  fontWeight: 700,
                }}
              >
                {(contact.customerName?.[0] || contact.customerPhone?.[0] || '?').toUpperCase()}
              </Avatar>

              <NameEditor
                name={contact.customerName || ''}
                canEdit={canEdit}
                onSave={(next) => saveField({ name: next }, 'Name')}
                fallback={formatPhoneDisplay(contact.customerPhone)}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {flagEmoji(contact.customerCountryCode)} {formatPhoneDisplay(contact.customerPhone)}
              </Typography>
              {contact.customerEmail && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {contact.customerEmail}
                </Typography>
              )}

              <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap mt={1.5}>
                {contact.channel === 'instagram' ? (
                  <Chip size="small" label="Instagram" />
                ) : (
                  <SourceChip source="whatsapp" />
                )}
                <StatusChip status={contact.status} dot sx={{ textTransform: 'capitalize' }} />
                {contact.optOut?.isOptedOut && <Chip size="small" color="warning" label="Opted out" />}
              </Stack>

              {contact.source && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                  Source: {contact.source}
                  {contact.sourceDetail ? ` · ${contact.sourceDetail}` : ''}
                </Typography>
              )}

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}
              >
                <Box>
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.6875rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Customer since
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {fmtDate(contact.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.6875rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Last active
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {contact.lastInteractionAt || contact.lastMessageAt ? timeAgo(contact.lastInteractionAt || contact.lastMessageAt || '') : '—'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<QuestionAnswerOutlined />}
                  onClick={() =>
                    navigate(
                      `${pathPrefix}/activity/chat-logs?conversationId=${contact._id}&phone=${encodeURIComponent(
                        contact.customerPhone
                      )}`
                    )
                  }
                >
                  Open chat
                </Button>
                <Button fullWidth variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}>
                  Export CSV
                </Button>
                {canEdit && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color={isArchived ? 'primary' : 'inherit'}
                    startIcon={isArchived ? <CheckCircleOutline /> : <ArchiveIcon />}
                    onClick={() => setArchiveConfirm(true)}
                  >
                    {isArchived ? 'Restore contact' : 'Archive contact'}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Activity — notes plus the audit trail for this contact and any
              lead sharing its phone, merged newest-first server-side. Refetched
              after every successful edit on this page. */}
          <Card
            sx={{
              flex: { md: 1 },
              minHeight: { xs: 0, md: 220 },
              display: 'flex',
              flexDirection: 'column',
              borderColor: '#e8ecf3',
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '&:last-child': { pb: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                  <History fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, fontSize: '0.9375rem' }}>
                  Activity
                </Typography>
                <Tooltip title="Show assignment, escalation and hand-back events">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Routing
                    </Typography>
                    <Switch size="small" checked={showRouting} onChange={(_e, v) => setShowRouting(v)} />
                  </Stack>
                </Tooltip>
              </Box>

              {activityLoading && visibleActivity.length === 0 ? (
                <Stack spacing={1.5}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i}>
                      <Skeleton height={18} width="70%" />
                      <Skeleton height={14} width="40%" />
                    </Box>
                  ))}
                </Stack>
              ) : activity.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {showRouting ? 'No activity yet.' : 'No notes or edits yet — turn on Routing to see assignment events.'}
                </Typography>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1, maxHeight: { xs: 420, md: 'none' } }}>
                  {visibleActivity.map((item, index) => (
                    <Box key={item.id} sx={{ display: 'flex', gap: 1.5 }}>
                      {/* Dot + connector: the connector is drawn as the rail
                          BELOW each dot, so the last row has none and the line
                          can never run past the final entry. */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha('#4f46e5', 0.1),
                            color: '#4f46e5',
                          }}
                        >
                          {activityIcon(item)}
                        </Box>
                        {index < visibleActivity.length - 1 && (
                          <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', my: 0.5 }} />
                        )}
                      </Box>

                      <Box sx={{ pb: index < visibleActivity.length - 1 ? 2 : 0, minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {describeActivity(item)}
                        </Typography>
                        {item.kind === 'note' && item.text && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.text}
                          </Typography>
                        )}
                        <Tooltip title={fmtDateTime(item.at)}>
                          <Typography variant="caption" color="text.secondary">
                            by {item.actor.name} · {timeAgo(item.at)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}

                  {activityHasMore && activityLimit < ACTIVITY_MAX_LIMIT && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      sx={{ position: 'sticky', bottom: 0, pt: 1, pb: 0.5, bgcolor: 'background.paper' }}
                    >
                      <Button
                        size="small"
                        disabled={activityLoading}
                        onClick={() =>
                          setActivityLimit(nextActivityLimit)
                        }
                      >
                        {activityLoading ? 'Loading…' : 'Show more'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
          </Box>
        </Grid>

        {/* ── RIGHT: the CRM record ──────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          {/* a. Details */}
          <SectionCard icon={<InfoOutlined fontSize="small" />} title="Details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <PhoneEditRow
                  contactId={id}
                  value={contact.customerPhone || ''}
                  canEdit={canEdit}
                  onSave={(digits) => patchContact({ phone: digits }, 'Phone')}
                  onOpenContact={(other) => navigate(`${pathPrefix}/contacts/${other}`)}
                />
                <DetailRow
                  label="Email"
                  value={contact.customerEmail || ''}
                  {...(canEdit ? { onSave: (next: string) => saveField({ email: next }, 'Email') } : {})}
                />
                <CountryEditRow
                  value={contact.customerCountryCode}
                  fallbackName={contact.customerCountry}
                  phoneDigits={normalizedPhone}
                  canEdit={canEdit}
                  onSave={(code) => patchContact({ countryCode: code }, 'Country')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow
                  label="Source"
                  value={[contact.source, contact.sourceDetail].filter(Boolean).join(' · ')}
                />
                {/* Assigned to — editable via PUT /admin/conversations/:id/assign
                    (adminApi.assignConversation), same endpoint the Contacts
                    list's quick-assign and bulk-assign actions use. */}
                <Box sx={{ py: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.25 }}
                  >
                    Assigned to
                  </Typography>
                  {editingAssignee ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                      <FormControl size="small" fullWidth>
                        {/* No floating label + displayEmpty/renderValue: with the old
                            "Team member" label an empty ("Unassigned") value rendered as a
                            blank white box, which read as a broken control. */}
                        <Select
                          displayEmpty
                          value={assigneeDraft}
                          onChange={(e) => setAssigneeDraft(e.target.value)}
                          disabled={assignSaving}
                          renderValue={(v) => {
                            if (!v) return <Box component="span" sx={{ color: 'text.disabled' }}>Unassigned</Box>;
                            const m = teamMembers.find((t) => t._id === v);
                            return m ? `${m.firstName} ${m.lastName}`.trim() || m.email : String(v);
                          }}
                        >
                          <MenuItem value="">
                            <Box component="span" sx={{ color: 'text.secondary' }}>Unassigned</Box>
                          </MenuItem>
                          {teamMembers.map((m) => (
                            <MenuItem key={m._id} value={m._id}>
                              {`${m.firstName} ${m.lastName}`.trim() || m.email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleAssignContact}
                        disabled={assignSaving || assigneeDraft === assigneeOriginal}
                      >
                        {assignSaving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEditingAssignee(false)}
                        disabled={assignSaving}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={assigneeName ? 'text.primary' : 'text.disabled'}
                      >
                        {assigneeName || 'Unassigned'}
                      </Typography>
                      {canEdit && (
                        <Tooltip title="Edit assigned to">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const orig = contact.assignedTo?._id || '';
                              setAssigneeDraft(orig);
                              setAssigneeOriginal(orig);
                              setEditingAssignee(true);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Box>
                <DetailRow label="Created" value={fmtDateTime(contact.createdAt)} />
                <DetailRow
                  label="Last activity"
                  value={fmtDateTime(contact.lastInteractionAt || contact.lastMessageAt)}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* b. Tags — same editor the inbox side panel uses. */}
          <SectionCard icon={<LocalOffer fontSize="small" />} title="Tags">
            <ContactTagsEditor
              contactId={contact._id}
              tags={contact.tags || []}
              canEdit={canEdit}
              onSaved={handleTagsSaved}
            />
          </SectionCard>

          {/* c. Custom fields */}
          <SectionCard icon={<Tune fontSize="small" />} title="Custom fields">
            {definitions.length === 0 && freeFormKeys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No custom fields on this contact yet.
              </Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  <DynamicFields
                    definitions={definitions}
                    values={definedValues}
                    onChange={(key, value) => setDefinedValues((prev) => ({ ...prev, [key]: value }))}
                  />
                  {freeFormKeys.map((key) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={key}
                        value={freeFormValues[key] ?? ''}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setFreeFormValues((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
                {canEdit && (
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button size="small" variant="contained" onClick={saveCustomFields} disabled={attrSaving}>
                      {attrSaving ? 'Saving…' : 'Save custom fields'}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {externalAttributes.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                  From integrations
                </Typography>
                {externalAttributes.map((ext) => (
                  <Box key={ext.clientId} mb={1.5}>
                    <Chip size="small" variant="outlined" label={ext.clientName || ext.clientId} sx={{ mb: 0.75 }} />
                    <Stack spacing={0.25}>
                      {(ext.attributes || []).map((attr) => (
                        <Typography key={attr.key} variant="body2" color="text.secondary">
                          {attr.key}: {String(attr.value)}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </>
            )}
          </SectionCard>

          {/* d. Sales pipeline */}
          {canSeeLeads && (
            <SectionCard
              icon={<TrendingUp fontSize="small" />}
              title="Sales pipeline"
              action={
                leads.length > 0 ? (
                  <Button
                    size="small"
                    onClick={() =>
                      navigate(
                        // `status=all` is explicit: the list defaults to only
                        // untriaged enquiries, which would filter the very lead
                        // this link is pointing at straight back out.
                        `${pathPrefix}/activity/leads?search=${encodeURIComponent(normalizedPhone)}&status=all`
                      )
                    }
                  >
                    Open lead
                  </Button>
                ) : undefined
              }
            >
              {leadsLoading ? (
                <Skeleton height={60} />
              ) : leads.length === 0 ? (
                <Stack spacing={1.5} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    No lead for this contact yet.
                  </Typography>
                  {canEditLeads && (
                    <Button size="small" variant="outlined" onClick={handleCreateLead} disabled={creatingLead}>
                      {creatingLead ? 'Creating…' : 'Create lead'}
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack spacing={3} divider={<Divider />}>
                  {leads.map((lead) => {
                    const terminal = (TERMINAL_STAGES as readonly string[]).includes(lead.status);
                    const busy = leadSavingId === lead._id;
                    const editable = canEditLeads && !busy;
                    const score = typeof lead.score === 'number' ? Math.max(0, Math.min(100, lead.score)) : null;
                    const scoreTone = score === null ? '#94a3b8' : (PRIORITY_TONE[lead.priority || 'low']?.fg ?? '#94a3b8');
                    return (
                      <Box key={lead._id}>
                        {/* Title row */}
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={700} noWrap>
                              {/* `title` is only ever set by manual/CSV creation —
                                  AI- and widget-captured leads populate `interest`
                                  instead, so falling straight to "Untitled lead"
                                  hid a perfectly good label on most real leads. */}
                              {lead.title || lead.interest || 'Untitled lead'}
                            </Typography>
                            {lead.createdAt && (
                              <Typography variant="caption" color="text.secondary">
                                Created {fmtDate(lead.createdAt)}
                              </Typography>
                            )}
                          </Box>
                          {terminal ? (
                            <StatusChip
                              status={lead.status}
                              dot
                              size="medium"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          ) : (
                            canEditLeads && (
                              <Button
                                size="small"
                                color="inherit"
                                disabled={busy}
                                endIcon={<KeyboardArrowDown fontSize="small" />}
                                onClick={(e) => setCloseMenu({ el: e.currentTarget, leadId: lead._id })}
                                sx={{ color: 'text.secondary' }}
                              >
                                Close lead
                              </Button>
                            )
                          )}
                        </Stack>

                        {/* Stage path. A terminal lead (lost / unqualified / archived) has left
                            the forward path, so instead of a bar with nothing highlighted it
                            gets a one-line outcome with a way back in. */}
                        {terminal ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #dbe1ea' }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              This lead is closed as <b style={{ textTransform: 'capitalize' }}>{lead.status}</b>.
                            </Typography>
                            {canEditLeads && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                onClick={() => patchLead(lead._id, { status: 'new' })}
                              >
                                Reopen
                              </Button>
                            )}
                          </Stack>
                        ) : (
                          <PipelineStageBar
                            stages={PIPELINE_STAGES}
                            current={lead.status}
                            disabled={!editable}
                            onSelect={(stage) => patchLead(lead._id, { status: stage })}
                          />
                        )}

                        {/* Properties */}
                        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} sm={6}>
                            <MiniLabel>Priority (from score)</MiniLabel>
                            <PriorityBadge value={lead.priority || 'low'} />
                          </Grid>
                          <Grid item xs={7} sm={4}>
                            <MiniLabel>Lead score</MiniLabel>
                            {score === null ? (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <LinearProgress
                                  variant="determinate"
                                  value={score}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 99,
                                    bgcolor: '#eef1f6',
                                    '& .MuiLinearProgress-bar': { bgcolor: scoreTone, borderRadius: 99 },
                                  }}
                                />
                                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'right' }}>
                                  {score}
                                </Typography>
                              </Stack>
                            )}
                          </Grid>
                          <Grid item xs={5} sm={2}>
                            <MiniLabel>Value</MiniLabel>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={typeof lead.estimatedValue === 'number' ? 'text.primary' : 'text.disabled'}
                            >
                              {typeof lead.estimatedValue === 'number' ? lead.estimatedValue.toLocaleString() : '—'}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Every submission behind this lead. The card above is a
                            ROLLUP — it shows only the latest state, so a contact
                            who enquired repeatedly looked identical to one who
                            enquired once. Collapsed by default to keep the
                            pipeline card scannable. */}
                        {(leadEnquiries[lead._id]?.length ?? 0) > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 2.5,
                              border: '1px solid #e8ecf3',
                              borderRadius: '10px !important',
                              bgcolor: '#fcfdff',
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
                              <Typography variant="body2" fontWeight={600}>
                                Enquiry history{' '}
                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                  · {leadEnquiries[lead._id]?.length}
                                </Box>
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Stack spacing={1.25}>
                                {leadEnquiries[lead._id]?.map((enq) => (
                                  <Box
                                    key={enq._id}
                                    sx={{ border: '1px solid #e8ecf3', borderRadius: 2, p: 1.5, bgcolor: '#fff' }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      useFlexGap
                                      sx={{ mb: 0.5 }}
                                    >
                                      {enq.source && <SourceChip source={enq.source} />}
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(enq.createdAt).toLocaleString()}
                                      </Typography>
                                    </Stack>
                                    {(enq.interest || enq.title) && (
                                      <Typography variant="body2" fontWeight={600}>
                                        {enq.interest || enq.title}
                                      </Typography>
                                    )}
                                    {enq.description && (
                                      <Typography variant="body2" color="text.secondary">
                                        {enq.description}
                                      </Typography>
                                    )}
                                    {enq.budget && (
                                      <Typography variant="caption" color="text.secondary">
                                        Budget: {enq.budget}
                                      </Typography>
                                    )}
                                    {enq.notes && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                                      >
                                        {enq.notes}
                                      </Typography>
                                    )}
                                    {enq.tags && enq.tags.length > 0 && (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mt: 0.75 }}
                                      >
                                        {enq.tags.map((t) => (
                                          <Chip key={t} size="small" label={t} />
                                        ))}
                                      </Stack>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </SectionCard>
          )}

          {/* e. Notes — same editor the inbox side panel uses. The notes came
              down with the contact, so this must not re-fetch them. */}
          <SectionCard icon={<StickyNote2Outlined fontSize="small" />} title="Notes">
            <ContactNotesEditor
              contactId={contact._id}
              canEdit={canEdit}
              canDelete={canDeleteNotes}
              initialNotes={contact.notes || []}
              onChanged={refreshActivity}
            />
          </SectionCard>

          {/* f. Appointments */}
          {canSeeAppointments && (
            <SectionCard
              icon={<EventOutlined fontSize="small" />}
              title="Appointments"
              action={
                appointments.length > 0 ? (
                  <Button
                    size="small"
                    onClick={() =>
                      navigate(`${pathPrefix}/appointments?phone=${encodeURIComponent(normalizedPhone)}`)
                    }
                  >
                    View all
                  </Button>
                ) : undefined
              }
            >
              {appointmentsLoading ? (
                <Stack spacing={1.25}>
                  <Skeleton variant="rounded" height={72} />
                  <Skeleton variant="rounded" height={72} />
                </Stack>
              ) : appointments.length === 0 ? (
                <Stack alignItems="center" spacing={0.75} sx={{ py: 3, color: 'text.secondary' }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f1f5f9',
                    }}
                  >
                    <EventAvailableOutlined sx={{ color: 'text.disabled' }} />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    No appointments yet
                  </Typography>
                  <Typography variant="caption">
                    Bookings made in chat or by your team will show up here.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1.25}>
                  {appointments.map((appt) => (
                    <AppointmentRow
                      key={appt._id}
                      appt={appt}
                      onOpen={() =>
                        navigate(`${pathPrefix}/appointments?phone=${encodeURIComponent(normalizedPhone)}`)
                      }
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          )}

          {/* g. Campaign engagement — absent entirely on a contact no campaign
              has ever targeted, so the whole card is hidden rather than showing
              a row of zeroes that would read as "targeted, never delivered". */}
          {hasEngagement && engagement && (
            <SectionCard icon={<CampaignOutlined fontSize="small" />} title="Campaign engagement">
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <DetailRow label="Campaigns targeted" value={String(engagement.targetedCampaignIds?.length ?? 0)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DetailRow label="Attempts" value={String(engagement.attempts ?? 0)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DetailRow label="Delivered" value={String(engagement.delivered ?? 0)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DetailRow label="Failed" value={String(engagement.failed ?? 0)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DetailRow label="Last sent" value={fmtDateTime(engagement.lastSentAt)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DetailRow label="Last read" value={fmtDateTime(engagement.lastReadAt)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DetailRow label="Last status" value={engagement.lastStatus || ''} />
                </Grid>
              </Grid>
            </SectionCard>
          )}
        </Grid>
      </Grid>

      <Menu anchorEl={closeMenu?.el ?? null} open={Boolean(closeMenu)} onClose={() => setCloseMenu(null)}>
        {(TERMINAL_STAGES as readonly string[]).map((stage) => (
          <MenuItem
            key={stage}
            sx={{ textTransform: 'capitalize' }}
            onClick={() => {
              const leadId = closeMenu?.leadId;
              setCloseMenu(null);
              if (leadId) void patchLead(leadId, { status: stage });
            }}
          >
            {stage === 'unqualified' ? 'Not a fit (unqualified)' : stage}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={archiveConfirm} onClose={() => !archiveSaving && setArchiveConfirm(false)}>
        <DialogTitle>{isArchived ? 'Restore contact' : 'Archive contact'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isArchived
              ? 'This contact will be active again and can be targeted by campaigns.'
              : 'Archived contacts are excluded from campaign sends. You can restore them at any time.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirm(false)} disabled={archiveSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleArchiveToggle} disabled={archiveSaving}>
            {archiveSaving ? 'Saving…' : isArchived ? 'Restore' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast('')}>
        <Alert severity="success" onClose={() => setToast('')}>
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(actionError)} autoHideDuration={5000} onClose={() => setActionError('')}>
        <Alert severity="error" onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/** Inline name edit on the contact card — pencil swaps the heading for a field. */
const NameEditor: React.FC<{
  name: string;
  fallback: string;
  canEdit: boolean;
  onSave: (next: string) => Promise<void>;
}> = ({ name, fallback, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(name);
  }, [name]);

  if (editing) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        <TextField
          size="small"
          autoFocus
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
        />
        <IconButton
          size="small"
          color="primary"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(draft.trim());
              setEditing(false);
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
        </IconButton>
        <IconButton size="small" disabled={saving} onClick={() => setEditing(false)}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      <Typography variant="h6" fontWeight={700} noWrap>
        {name || fallback}
      </Typography>
      {canEdit && (
        <Tooltip title="Edit name">
          <IconButton size="small" onClick={() => setEditing(true)}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default ContactDetailPage;
